import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { href: "/", label: "Home" },
    { href: "/over-rabia", label: "Over Rabia" },
    { href: "/diensten", label: "Aanpak" },
    { href: "/contact", label: "Contact" },
  ];

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-mist/90 backdrop-blur-md shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="container-custom">
        <nav className="flex items-center justify-between h-20 md:h-24">
          {/* Logo */}
          <Link
            to="/"
            data-testid="header-logo"
            className="flex items-center gap-2"
          >
            <span className="font-serif text-xl md:text-2xl font-medium text-burgundy tracking-tight">
              Regie op Cashflow
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-10">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`nav-link-${link.href.replace("/", "") || "home"}`}
                className={`text-sm font-medium tracking-wide transition-colors duration-300 gold-underline ${
                  location.pathname === link.href
                    ? "text-burgundy"
                    : "text-navy hover:text-burgundy"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* CTA Button - Red Berry */}
          <div className="hidden lg:block">
            <a
              href="https://calendly.com/rabia-regieopcashflow/30min"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-cta-button"
              className="bg-cta text-white px-6 py-3 text-xs tracking-widest uppercase font-semibold hover:bg-cta-dark transition-all duration-300 hover:shadow-lg"
            >
              Plan een gesprek
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-button"
            className="lg:hidden p-2 text-burgundy"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden bg-mist border-t border-sand"
            data-testid="mobile-menu"
          >
            <div className="container-custom py-6">
              <div className="flex flex-col gap-4">
                {navLinks.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    data-testid={`mobile-nav-${link.href.replace("/", "") || "home"}`}
                    className={`text-lg font-medium py-2 ${
                      location.pathname === link.href
                        ? "text-burgundy"
                        : "text-navy"
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
                <a
                  href="https://calendly.com/rabia-regieopcashflow/30min"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="mobile-cta-button"
                  className="mt-4 bg-cta text-white px-6 py-4 text-center text-sm tracking-widest uppercase font-semibold"
                >
                  Plan een gesprek
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
